/**
 * HeroDashboardMockup — restored to match production cartieai.com hero panel.
 *
 * Rendered on the right side of the landing hero. Replaces the
 * tail-f LiveTerminalHero per user request: "I want it like cartieai.com
 * but with updated data".
 *
 * Layout (top → bottom):
 *   1. Browser chrome: 3 mac dots + URL bar with "app.cartieai.com/dashboard"
 *      and a small "Sample data" pill
 *   2. Floating accent pills: "Anomaly Detected" (amber, top-left)
 *      and "AI Analysis Ready" (emerald, top-right)
 *   3. 3 KPI tiles: Monthly Spend $298K (-12%) · Saved This Month $47K (+38%)
 *      · Active Alerts 4 (2 critical)
 *   4. "Savings Trend · Live" bar chart with ▲+234.5% delta
 *   5. Token-leak event tile with "T1 · Leak" badge
 *   6. Three metric strip below the dashboard:
 *      "11.5/12 FinOps pains solved" · "27 Live engines" · "5 min To first insight"
 *   7. "Live signal · last 60 min" feed with 4 timestamped events
 *
 * All numbers are PURE SAMPLE DATA — clearly labeled in the URL bar pill.
 * The bar chart heights are deterministic (not random) so the layout
 * is stable across renders.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, AlertOctagon, Bell, Sparkles, ArrowRight, Zap } from 'lucide-react';
import WhyDollar from './WhyDollar';

// Deterministic bar heights for the savings-trend chart (24 bars).
// v34.18 (Feb 2026): replaced the monotonic upward trend with a more
// realistic peak-and-valley pattern that matches the reference look. The
// overall trajectory is still positive (matches ▲+234.5% delta).
const BAR_HEIGHTS = [42, 56, 48, 62, 52, 68, 58, 72, 64, 78, 70, 84, 76, 92, 82, 96, 86, 100, 90, 98, 88, 94, 84, 100];

// v34.19 (Feb 2026): emerald-blueish alternating mix per user feedback —
// production cartieai.com bars alternate between emerald and sky so the
// chart reads as "blueish-emerald", not pure green. Pattern is hand-tuned
// (not strict alternation) to feel organic, not stripey.
// v34.61 (Feb 2026) — bar mix was emerald + sky-blue which read as off-brand
// and combined with the underneath halo caused a "glare" effect under the
// chart. Switched the secondary stop from sky → teal (brand-family) and
// removed the halo entirely. Cleaner, more on-brand, no glare.
//   E = emerald-400 (#34D399)   T = teal-400 (#2DD4BF)
const BAR_COLORS = ['E','T','E','E','T','E','T','T','E','T','E','E','T','E','T','E','T','E','T','E','E','T','E','T'];
const COLOR_MAP = {
  E: { bar: 'bg-emerald-400', glow: 'shadow-[0_0_10px_-1px_rgba(52,211,153,0.5)]' },
  T: { bar: 'bg-teal-400',    glow: 'shadow-[0_0_10px_-1px_rgba(45,212,191,0.5)]' },
};

const KPI_TILES = [
  { label: 'Monthly Spend',     value: '$298K', amount: 298000, ctx: 'Total AWS + Azure + Snowflake + Databricks monthly cloud + AI spend tracked by CARTIE for a typical Series A SaaS', delta: '-12%', deltaDir: 'down', tone: 'emerald', why: true },
  { label: 'Saved This Month',  value: '$47K',  amount: 47000,  ctx: 'Total monthly cost saved by CARTIE across all 26 engines (semantic cache, prompt compactor, retry storm detector, idle pod hunter, etc.)', delta: '+38%', deltaDir: 'up', tone: 'emerald', why: true },
  { label: 'Active Alerts',     value: '4',     delta: '2 critical', deltaDir: 'flat', tone: 'amber' },
];

// v34.63 — was hardcoded clock times (11:42, 11:18, 10:51, 10:32) that stayed
// the same at 3am. Now stored as MINUTES_AGO and rendered relative to "now" so
// the "auto-refreshes hourly" label stops being a lie.
const LIVE_EVENTS = [
  { minutesAgo: 3,  text: 'Token leak caught on /api/chat — $420 saved',                tone: 'rose' },
  { minutesAgo: 27, text: 'RDS rightsize approved by team lead — $284/mo',              tone: 'emerald' },
  { minutesAgo: 54, text: 'PR #4421 blocked by budget negotiator (Red band)',           tone: 'amber' },
  { minutesAgo: 73, text: 'Egress preflight: moved Snowflake → Bedrock to in-region',   tone: 'sky' },
];

const TONE = {
  emerald: { text: 'text-emerald-300', bg: 'bg-emerald-500',  border: 'border-emerald-500/40', dot: 'bg-emerald-400' },
  amber:   { text: 'text-amber-300',   bg: 'bg-amber-500',    border: 'border-amber-500/40',   dot: 'bg-amber-400' },
  rose:    { text: 'text-rose-300',    bg: 'bg-rose-500',     border: 'border-rose-500/40',    dot: 'bg-rose-400' },
  sky:     { text: 'text-sky-300',     bg: 'bg-sky-500',      border: 'border-sky-500/40',     dot: 'bg-sky-400' },
};

export default function HeroDashboardMockup() {
  return (
    <div className="relative" data-testid="hero-dashboard-mockup">

      {/* Floating accent pills above the browser. Echo cartieai.com production. */}
      <div className="absolute -top-3 left-4 z-20 inline-flex items-center gap-1.5 bg-amber-500/15 border border-amber-500/40 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] text-amber-200 shadow-lg shadow-amber-500/20" data-testid="hero-pill-anomaly">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-400" />
        </span>
        Anomaly Detected
      </div>
      <div className="absolute -top-3 right-4 z-20 inline-flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500/40 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-200 shadow-lg shadow-emerald-500/20" data-testid="hero-pill-ai-ready">
        <Sparkles className="w-3 h-3" />
        AI Analysis Ready
      </div>

      {/* Browser chrome */}
      <div className="relative bg-slate-950 border border-white/10 rounded-xl overflow-hidden shadow-2xl shadow-emerald-500/5">
        {/* v34.63 — prominent SAMPLE PREVIEW watermark so visitors don't read
            the numbers as real customer data. The tiny URL-bar tag was easy to
            miss; this corner ribbon stays subtle but unmistakable. */}
        <div className="absolute top-3 right-3 z-30 pointer-events-none">
          <div className="inline-flex items-center gap-1.5 bg-amber-500/95 text-slate-950 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-widest uppercase shadow-lg shadow-amber-500/30" data-testid="hero-sample-preview-ribbon">
            <Sparkles className="w-3 h-3" />
            Sample preview
          </div>
        </div>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] bg-slate-900">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
          </div>
          <div className="flex-1 mx-3 px-3 py-1 rounded bg-slate-900 border border-white/[0.04] text-[11px] font-mono text-slate-500 flex items-center">
            <span>app.cartieai.com/dashboard</span>
          </div>
        </div>

        {/* Dashboard body */}
        <div className="p-4 sm:p-5 bg-slate-950">
          {/* KPI tiles — Saved tile + Monthly Spend tile have a "Why?"
              LLM affordance (v34.31, Feb 2026) so visitors can hover and
              get a 2-sentence explanation of how the number was computed. */}
          <div className="grid grid-cols-3 gap-2.5 mb-4" data-testid="hero-kpi-tiles">
            {KPI_TILES.map((k) => {
              const t = TONE[k.tone];
              const valueNode = (
                <div className="font-display font-black text-white text-xl sm:text-2xl tabular-nums tracking-tight leading-none">{k.value}</div>
              );
              return (
                <div key={k.label} className="bg-slate-900 border border-white/[0.05] rounded-md p-3">
                  <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">{k.label}</div>
                  {k.why ? (
                    <WhyDollar amount={k.amount} context={k.ctx} side="bottom">
                      {valueNode}
                    </WhyDollar>
                  ) : (
                    valueNode
                  )}
                  <div className={`mt-1.5 text-[10px] font-mono ${t.text}`}>
                    {k.deltaDir === 'up' && <TrendingUp className="w-2.5 h-2.5 inline mr-0.5" />}
                    {k.deltaDir === 'down' && <TrendingUp className="w-2.5 h-2.5 inline mr-0.5 rotate-180" />}
                    {k.delta}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Savings trend bar chart — v34.18 (Feb 2026): redesigned to
              match production cartieai.com — solid emerald-400 bars with a
              soft emerald glow underneath. The previous rainbow gradient
              (emerald→teal→cyan) was making the chart look "ugly" per user
              feedback. Now bars look like uniform LED indicators with a
              tasteful reflection halo at the base. */}
          <div className="bg-slate-900 border border-white/[0.05] rounded-md p-3 mb-3" data-testid="hero-savings-chart">
            <div className="flex items-center justify-between mb-2.5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 inline-flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                </span>
                Savings Trend · Live
              </div>
              <span className="text-[11px] font-mono font-bold text-emerald-300">▲ +234.5%</span>
            </div>
            <div className="relative">
              <div className="flex items-end justify-between gap-[3px] h-20 relative z-10">
                {BAR_HEIGHTS.map((h, i) => {
                  const c = COLOR_MAP[BAR_COLORS[i]] || COLOR_MAP.E;
                  return (
                    <div
                      key={i}
                      className={`flex-1 rounded-[2px] ${c.bar} ${c.glow}`}
                      style={{ height: `${h}%` }}
                    />
                  );
                })}
              </div>
              {/* v34.61 — decorative halo REMOVED. Was a blurred emerald+sky
                  gradient strip under the chart that created the "glare" the
                  user spotted. The bars themselves now provide enough visual
                  warmth via their per-bar shadow glow (see COLOR_MAP). */}
            </div>
          </div>

          {/* Token leak event tile */}
          <div className="bg-rose-500/[0.08] border border-rose-500/30 rounded-md p-3 flex items-start gap-3" data-testid="hero-leak-event">
            <div className="w-9 h-9 bg-rose-500 flex items-center justify-center shrink-0 rounded-sm shadow-md shadow-rose-500/30">
              <Zap className="w-4 h-4 text-black" strokeWidth={2.6} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-white text-sm">Token leak caught</div>
              <div className="text-[11px] text-slate-400 mt-0.5">$420 saved · agent retry storm on /chat</div>
            </div>
            <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-rose-300 bg-rose-500/10 border border-rose-500/40 px-1.5 py-0.5 self-start">T1 · Leak</span>
          </div>
        </div>
      </div>

      {/* v34.17 (Feb 2026): three-metric strip removed per user feedback —
          the "11.5/12 · 26 · 5 min" numbers under the dashboard were
          dominating with no real context. Same proof shows up later in the
          page via the ScorecardTeaser + Engines catalog. */}

      {/* Live signal feed */}
      <div className="mt-4 bg-slate-950 border border-white/[0.05] rounded-md overflow-hidden" data-testid="hero-live-signal">
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.05]">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-400">
            <Bell className="w-3 h-3 text-emerald-400" />
            <span>Live signal</span>
            <span className="text-slate-700">·</span>
            <span>last 60 min</span>
          </div>
          <span className="text-[9px] font-mono text-slate-600 uppercase tracking-[0.18em]">auto-refreshes hourly</span>
        </div>
        <div className="divide-y divide-white/[0.04]">
          {LIVE_EVENTS.map((e) => {
            const t = TONE[e.tone];
            // v34.63 — relative time so the "auto-refreshes hourly" label is true.
            const label = e.minutesAgo < 60 ? `${e.minutesAgo}m ago` : `${Math.floor(e.minutesAgo / 60)}h ago`;
            return (
              <div key={e.minutesAgo + e.text} className="px-4 py-2 flex items-start gap-3 text-[11.5px]">
                <span className="font-mono text-slate-500 tabular-nums shrink-0 w-12 text-right">{label}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${t.dot} mt-1.5 shrink-0`} />
                <span className="text-slate-300 leading-relaxed">{e.text}</span>
              </div>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t border-white/[0.05] flex items-center justify-between">
          <span className="text-[10px] text-slate-600 font-mono">Sample telemetry</span>
          <Link to="/login" className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-300 hover:text-emerald-200 transition-colors" data-testid="hero-see-stack-link">
            See your stack <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
